<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';
import CoreIcon, { CoreIconProps } from '@core/core-icon.vue';

export interface SelectItem {
	label: string,
	value: string
};

const props = defineProps<{
	name: string,
	icon?: CoreIconProps,
	list: SelectItem[],
	toTop?: boolean,
	placeholder?: string
}>();

const modelValue = defineModel();

const isOpen = ref(false);
const container = useTemplateRef('container');

const headerTitle = computed(() => {
	const item = props.list.find(item => item.value === modelValue.value);
	return item?.label || props.placeholder;
});
const modes = computed(() => ({
	'form-select_open': isOpen.value,
	'form-select_to_top': props.toTop
}));

const toggle = () => {
	isOpen.value = !isOpen.value;
}
const hide = () => {
	isOpen.value = false;
}
const show = () => {
	isOpen.value = true;
}

const outsideClickHandler = (event: MouseEvent) => {
	if (!container.value) {
		return false;
	}
	const withinBoundaries = event.composedPath().includes(container.value);

	if (!withinBoundaries) {
		hide();
	}
};

onMounted(() => {
	document.addEventListener('click', outsideClickHandler);
});
onBeforeUnmount(() => {
	document.removeEventListener('click', outsideClickHandler);
});
</script>

<template>
	<div
		class="form-select"
		:class="modes"
		ref="container"
	>
		<div class="form-select__header" @click="toggle">
			<core-icon v-if="icon" v-bind="icon" class="form-select__icon" />
			<span class="form-select__title font-16-pro-regular">{{ headerTitle }}</span>
			<core-icon name="arrowDown" viewBox="0 0 11 8" class="form-select__arrow" />
		</div>
		<div class="form-select__body">
			<ul class="form-select__list">
				<li
					v-for="(item, index) in list"
					:key="item.value"
					class="form-select__list-item"
				>
					<label
						class="form-select__label"
						:class="{'is-active': item.value === modelValue}"
						@click="hide"
					>
						<input
							type="radio"
							:name="name"
							:value="item.value"
							class="form-select__input"
							v-model="modelValue"
						/>
						<span class="form-select__title font-16-pro-regular">
							{{ item.label }}
						</span>
					</label>

				</li>
			</ul>
		</div>
	</div>
</template>

<style lang="scss">
.form-select {
	position: relative;
	user-select: none;

	// Подсветка там, где есть курсор, а не только на широком экране: в узком
	// окне на ноутбуке список тоже открывают мышью
	@media (hover: hover) and (pointer: fine) {
		&:hover {
			.form-select {
				&__header {
					border-color: rgba(var(--color-active-rgb), 0.3);
					background-color: rgba(var(--color-active-rgb), 0.06);
				}
			}
		}
	}
	&_open {
		.form-select {
			&__arrow {
				transform: rotate(-180deg);
			}
			&__body {
				visibility: visible;
				opacity: 1;
			}
		}
	}
	&_to_top {
		.form-select {
			&__body {
				top: auto;
				bottom: 100%;
				padding-top: 0;
				padding-bottom: 6px;
			}
		}
	}
	&__header {
		padding: 1.2rem;
		height: 100%;
		border-radius: 10rem;
		border: 1px solid rgba(var(--color-active-rgb), 0.12);
		transition: border-color 0.13s, background-color 0.13s;
		display: flex;
		align-items: center;
		gap: 1.6rem;
		cursor: pointer;

	}
	&__body {
		position: absolute;
		left: 0;
		width: 100%;
		top: 100%;
		padding-top: 3px;
		visibility: hidden;
		opacity: 0;
		transition: opacity 0.13s, visibility 0.13s;
	}
	&__icon {
		width: var(--icon-width, 2.4rem);
		fill: var(--icon-color, var(--color-active));
	}
	&__arrow {
		fill: currentColor;
		width: 1rem;
		margin-left: auto;
	}
	&__list {
		padding: 1.2rem;
		border-radius: 1.2rem;
		display: flex;
		gap: 1.6rem;
		flex-direction: column;
		background-color: rgba(var(--color-active-rgb), 0.06);
		@include theme-dark {
			background-color: rgba(var(--color-active-rgb), 0.12);
		}
	}
	&__list-item {
		display: block;
	}
	// Пункт списка с собственной подложкой при наведении; отрицательные поля
	// сохраняют прежнее положение текста
	&__label {
		display: block;
		cursor: pointer;
		padding: 0.4rem 0.8rem;
		margin: -0.4rem -0.8rem;
		border-radius: 0.8rem;
		transition: background-color 0.13s, color 0.13s;

		&.is-active {
			color: var(--color-active);
		}

		@media (hover: hover) and (pointer: fine) {
			&:hover {
				background-color: rgba(var(--color-active-rgb), 0.1);
				color: var(--color-active);
			}
		}

		@include theme-dark {
			&:not(.is-active) {
				color: rgba(var(--color-text-rgb), 0.5);
			}
		}
	}
	&__input {
		display: none;
	}
}
</style>
